import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useLocation as useRouterLocation } from "react-router-dom";
import queryString from "query-string";

import { Space, Typography, Form, Row, Col } from "antd";
import { useForm } from "antd/lib/form/Form";
import {
  FormInput,
  FormInputArea,
  FormButton,
  FormPassword,
  FormSelect,
  FormPhoneNumber,
  FormDatePicker,
} from "components/FormItems/FlatFormItems";
import moment from "moment";
import Geocode from "react-geocode";

import { CloseCircleOutlined, CheckOutlined } from "@ant-design/icons";
import { openNotificationWithIcon } from "utils/Notification";

import {
  createUser,
  updateUser,
  externalRegister,
  hasPendingRegistration,
  sendWelcomeEmail,
} from "services/auth.services";

import useLocation from "hooks/useLocation";
import LocationDropdown from "components/LocationDropDown/LocationDropdown";
import { getItem } from "utils/storage";
import { getStateAndCountryFromZip } from "services/locationCenter.services";

import PendingRegistrationModal from "./PendingRegistrationModal";
import "./RegisterForm.scss";

const COUNTRY_ID = 3;
function RegisterForm({ onSuccess, isUsernamePasswordRequired = true }) {
  const location = useRouterLocation();
  const qs = queryString.parse(location.search);

  const order = getItem("pendingOrder");

  const [basicInformationForm] = useForm();
  const { user, miscSettings } = useSelector((state) => ({
    user: state.user,
    intakeForms: state.intakeForms,
    miscSettings: state.misc.miscSettings,
  }));

  const { isRegisteringUser, isUpdatingUser, currentUser = {} } = user;
  const currentUserHasAccount =
    currentUser.email !== "" &&
    currentUser?.email !== null &&
    currentUser?.email !== undefined;
  const dispatch = useDispatch();

  const isLoadedRef = useRef(false);
  // last identity values sent to the pending-registration check, so we only call it on changes
  const lastCheckRef = useRef({ key: null, promise: null, settled: false, controller: null });
  // identity values shown in the welcome email modal, null when it is closed
  const [pendingRegistration, setPendingRegistration] = useState(null);
  const [sendingWelcomeEmail, setSendingWelcomeEmail] = useState(false);

  // resolves true when a new or still in-flight check finds a pending registration
  // (the welcome email prompt is shown); false when there is nothing new to act on
  const checkPendingRegistration = async () => {
    if (currentUserHasAccount) return false;

    const fields = ["firstName", "lastName", "email"];

    const formValues = basicInformationForm.getFieldsValue(fields);
    const values = fields.reduce((acc, field) => {
      acc[field] = formValues[field]?.trim?.() ?? "";
      return acc;
    }, {});

    if (fields.some((field) => !values[field])) return false;

    // case-insensitive, so changing only letter case does not trigger a new check
    const key = JSON.stringify(fields.map((field) => values[field].toLowerCase()));
    const lastCheck = lastCheckRef.current;
    if (key === lastCheck.key) return lastCheck.settled ? false : lastCheck.promise;

    // discard the previous request if it is still in flight
    lastCheck.controller?.abort();
    const check = { key, settled: false, controller: new AbortController() };
    check.promise = dispatch(
      hasPendingRegistration(values, check.controller.signal)
    ).then((exists) => {
      check.settled = true;
      // skip the prompt if the values changed while this request was in flight
      if (exists && lastCheckRef.current === check) {
        setPendingRegistration(values);
      }
      return exists;
    });
    lastCheckRef.current = check;
    return check.promise;
  };

  useEffect(() => {
    return () => lastCheckRef.current.controller?.abort();
  }, []);

  const handleSendWelcomeEmail = async () => {
    setSendingWelcomeEmail(true);
    const res = await dispatch(sendWelcomeEmail(pendingRegistration));
    setSendingWelcomeEmail(false);
    if (res?.status === "success") setPendingRegistration(null);
  };

  const handleIdentityBlur = () => {
    checkPendingRegistration();
  };

  useEffect(() => {
    if (isLoadedRef.current === true) {
      return;
    }

    if (order?.serviceAddress) {
      basicInformationForm.setFieldsValue({
        streetAddress1: order.serviceAddress || "",
      });
    } else if (currentUser?.streetAddress1) {
      basicInformationForm.setFieldsValue({
        streetAddress1: currentUser.streetAddress1 || "",
      });
    }
    else if (currentUser?.primaryAddress)
      basicInformationForm.setFieldsValue({
        streetAddress1: currentUser?.primaryAddress || "",
      });
    else {
      navigator.geolocation.getCurrentPosition(function (position) {
        Geocode.fromLatLng(
          position.coords.latitude,
          position.coords.longitude
        ).then((response) => {
          const serviceAddress = response.results[0].formatted_address;
          basicInformationForm.setFieldsValue({
            streetAddress1: serviceAddress || "",
          });
        });
      });
    }
    isLoadedRef.current = true;
  }, [order, currentUser]);

  const trafficSource = useMemo(() => {
    if (miscSettings) {
      const sourcesList = miscSettings.find((res) => {
        if (res.key == "MarketingSources") {
          return res;
        }
      });
      let sourceListString = decodeURI(sourcesList?.value || "");
      return JSON.parse(sourceListString || "[]");
    }
    return [];
  }, [miscSettings]);

  const handleBasicInformationSubmit = async (values) => {
    // set state, city and country of the user
    // values.city = cities.find((city) => city.locationId === values.cityId);
    // values.state = states.find((state) => state.locationId === values.stateId);
    // values.country = countries.find(
    //   (country) => country.locationId === values.countryId
    // );

    // autofill may skip the blur events, so make sure the current values were checked
    if (await checkPendingRegistration()) return;

    if (qs?.referrerCode) {
      values.referrerCode = qs.referrerCode;
    }
    values.userName = values.userName?.trim?.();
    values.dob = moment(values.dob).toISOString();

    const streetAddress1 = values.streetAddress1;
    const { state, country, zipCode } =
      (await getStateAndCountryFromZip(streetAddress1)) ?? {};

    if (!state || !country || !zipCode) {
      openNotificationWithIcon(
        "error",
        "Error!",
        "Please enter a valid street address"
      );
      return;
    }

    if (!currentUserHasAccount) {
      const cb = qs?.external ? externalRegister : createUser;

      await dispatch(cb({
        ...values,
        streetAddress1,
        state, country, zipCode
      }, decodeURIComponent(qs?.token))).then((res) => {
        if (res) {
          openNotificationWithIcon(
            "success",
            "User created",
            "User has been created, you can login with credentials."
          );
          onSuccess();
        }
      });
    } else {
      values.mobileNo = values.phoneNo; // api name mapping...
      await dispatch(updateUser(currentUser, { ...values, streetAddress1, state, country, zipCode })).then((res) => {
        if (res) {
          onSuccess();
        }
      });
    }
  };

  const streetAddress = Form.useWatch("streetAddress1", basicInformationForm);

  return (
    <Form
      autoComplete={"off"}
      layout="vertical"
      name="basicInformationForm"
      form={basicInformationForm}
      requiredMark={false}
      initialValues={{
        inviterId: qs.inviterId,
        inviterName: qs.inviterName,
        userName: currentUser?.userName || "",
        firstName: qs.firstName || currentUser?.firstName || "",
        lastName: qs.lastName || currentUser?.lastName || "",
        email: qs.email || currentUser?.email || "",
        phoneNo: currentUser?.phoneNumber || currentUser?.phoneNo || "",
        streetAddress1:
          currentUser?.streetAddress1 || order?.streetAddress1
            ? order?.streetAddress1
            : "",
        streetAddress2: currentUser?.streetAddress2 || "",
        countryId: currentUser?.countryId || COUNTRY_ID || undefined, // USA
        cityId: currentUser?.cityId || undefined,
        stateId: currentUser?.stateId || undefined,
        zipCode: currentUser?.zipCode || undefined,
        instructions: currentUser?.instructions || "",
        dob: currentUser?.dob ? moment(user.currentUser.dob) : undefined,
        password: "",
      }}
      onFinish={handleBasicInformationSubmit}
      onFinishFailed={({ values, errorFields, outOfDate }) => {
        basicInformationForm.scrollToField(errorFields[0].name, {
          scrollMode: "if-needed",
          block: "center",
          behavior: "smooth",
        });
      }}
    >
      <Row gutter={[24, 16]}>
        <Col xs={24} md={12} order={currentUserHasAccount ? 3 : 1}>
          <FormInput
            variant="underlined"
            name="firstName"
            onBlur={handleIdentityBlur}
            label="First Name"
            placeholder="First Name"
            required={true}
            formItemStyles={{ marginBottom: 0 }}
          />
        </Col>
        <Col xs={24} md={12} order={currentUserHasAccount ? 4 : 1}>
          <FormInput
            variant="underlined"
            name="lastName"
            onBlur={handleIdentityBlur}
            label="Last Name"
            placeholder="Last Name"
            required={true}
            formItemStyles={{ marginBottom: 0 }}
          />
        </Col>

        <Col xs={24} md={12} order={currentUserHasAccount ? 1 : 3}>
          <Row gutter={[0, 16]}>
            <Col span={24}>
              <FormInput
                readOnly={currentUserHasAccount}
                variant="underlined"
                name="email"
                onBlur={handleIdentityBlur}
                label="Email Address"
                type="email"
                placeholder="Email Address"
                formItemStyles={{ marginBottom: 0 }}
                required={true}
              // addonAfter={
              //   <Space className="signupAddonAfterPrimaryText">
              //     <CheckOutlined />
              //     <Typography>Primary</Typography>
              //   </Space>
              // }
              />
            </Col>
            {/* <Form.List name="emails">
              {(fields, { add, remove }) => {
                return (
                  <>
                    {fields.map((field, index) => {
                      return (
                        <Col span={24}>
                          <FormInput
                            variant="underlined"
                            name={[index, "email"]}
                            type="email"
                            label={`Email Address ${index + 2}`}
                            placeholder="Email Address"
                            formItemStyles={{ marginBottom: 0 }}
                            addonAfter={
                              <CloseCircleOutlined
                                style={{ cursor: "pointer" }}
                                onClick={() => remove(index)}
                              />
                            }
                          />
                        </Col>
                      );
                    })}
                    <Typography.Link onClick={add}>
                      + Add New Email Address
                    </Typography.Link>
                  </>
                );
              }}
            </Form.List> */}
          </Row>
        </Col>
        {isUsernamePasswordRequired && (
          <Col xs={24} md={12} order={currentUserHasAccount ? 2 : 4}>
            <FormInput
              readOnly={currentUserHasAccount}
              variant="underlined"
              name="userName"
              label="Username"
              placeholder="Username"
              formItemStyles={{ marginBottom: 0 }}
              required={true}
            // addonAfter={
            //   <Space className="signupAddonAfterPrimaryText">
            //     <CheckOutlined />
            //     <Typography>Primary</Typography>
            //   </Space>
            // }
            />
          </Col>
        )}

        {!currentUserHasAccount && isUsernamePasswordRequired && (
          <Col xs={24} md={12} order={5}>
            <FormPassword
              variant="underlined"
              name="password"
              label="Create Password"
              placeholder="Password"
              required={true}
              formItemStyles={{ marginBottom: 0 }}
            />
          </Col>
        )}

        <Col xs={24} md={12} order={6}>
          <Row gutter={[0, 16]}>
            <Col span={24}>
              <FormPhoneNumber
                variant="underlined"
                name="phoneNo"
                label="Mobile Number"
                placeholder="Mobile Number"
                formName="basicInformationForm"
                required={true}
                formItemStyles={{ marginBottom: 0 }}
                country="us"
              // addonAfter={
              //   <Space className="signupAddonAfterPrimaryText">
              //     <CheckOutlined />
              //     <Typography>Primary</Typography>
              //   </Space>
              // }
              />
            </Col>
            {/* <Form.List name="mobiles">
              {(fields, { add, remove }) => {
                return (
                  <>
                    {fields.map((field, index) => {
                      return (
                        <Col span={24}>
                          <FormInput
                            variant="underlined"
                            name={[index, "mobile"]}
                            label={`Mobile Number ${index + 2}`}
                            placeholder="Mobile Number"
                            formItemStyles={{ marginBottom: 0 }}
                            addonAfter={
                              <CloseCircleOutlined
                                style={{ cursor: "pointer" }}
                                onClick={() => remove(index)}
                              />
                            }
                          />
                        </Col>
                      );
                    })}
                    <Typography.Link onClick={add}>
                      + Add New Email Address
                    </Typography.Link>
                  </>
                );
              }}
            </Form.List> */}
          </Row>
        </Col>

        <Col xs={24} md={12} order={7}>
          <FormDatePicker
            form={basicInformationForm}
            name="dob"
            label="Date of Birth"
            variant="underlined"
            formItemStyles={{ marginBottom: 0 }}
            required
            validateFirst
            // defaultValue={moment("01-01-1994", "DD-MM-YYYY")}
            rules={[
              {
                required: true,
                message: "*Required",
              },
              // {
              //   message:
              //     "Your age should be between 18 and 85. Kindly contact our support.",
              //   validator: (_, value) => {
              //     if (
              //       value.isSameOrBefore(moment().subtract(18, "years")) &&
              //       value.isAfter(moment().subtract(85, "years"))
              //     )
              //       return Promise.resolve();
              //     else return Promise.reject();
              //   },
              // },
            ]}
            disabledDate={(current) =>
              current.isSameOrAfter(
                moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
              )
            }
          />
        </Col>
        {/* <Col xs={0} md={12}></Col> */}

        <Col xs={24} md={12} order={8}>
          {/* <FormInput
            variant="underlined"
            name="streetAddress1"
            label="Street Address"
            placeholder="Street address"
            required={true}
            formItemStyles={{ marginBottom: 0 }}
          /> */}
          <LocationDropdown
            required={true}
            inputValue={currentUser?.primaryAddress ?? order?.serviceAddress}
            name={"streetAddress1"}
            form={basicInformationForm}
            onChange={(e) => {
              basicInformationForm.setFieldsValue({
                streetAddress1: e?.target?.value || "",
              });
              basicInformationForm.validateFields(["streetAddress1"])

            }}
            clearData={() => {
              basicInformationForm.setFieldsValue({
                streetAddress1: "",
              });
              basicInformationForm.validateFields(["streetAddress1"])
            }}
            className={"flatFormItem underlined addressLocation"}
            label={"Street Address"}
            streetAddress={streetAddress}
          />
        </Col>
        {/* <Col xs={24} md={12}>
          <FormInput
            variant="underlined"
            name="streetAddress2"
            label="Street Address 2"
            placeholder="Street address"
            required={true}
            formItemStyles={{ marginBottom: 0 }}
          />
          <LocationDropdown
            name={"streetAddress2"}
            form={basicInformationForm}
            onChange={(facilityId, value) => {
              basicInformationForm.setFieldsValue({ streetAddress2: value });
            }}
            className={"flatFormItem underlined addressLocation"}
            label={"Street Address 2"}
          />
        </Col> */}
        {/* <Col xs={24} md={12}>
          <FormSelect
            variant="underlined"
            name="stateId"
            label="State"
            placeholder="State"
            required={true}
            formItemStyles={{ marginBottom: 0 }}
            options={states}
            renderLabel="locationName"
            renderValue="locationId"
            onChange={(value, option) => {
              basicInformationForm.setFieldsValue({ cityId: null });
              setState(value);
            }}
            showSearch={true}
            onSearch={(value) => { }}
            filterOption={(input, option) => {
              return option?.children
                ?.toLowerCase?.()
                .includes?.(input.toLowerCase());
            }}
          />
        </Col>
        <Col xs={24} md={12}>
          <FormSelect
            variant="underlined"
            name="cityId"
            label="City"
            placeholder="City"
            required={true}
            formItemStyles={{ marginBottom: 0 }}
            options={cities}
            renderLabel="locationName"
            renderValue="locationId"
            showSearch={true}
            onSearch={(value) => { }}
            filterOption={(input, option) => {
              console.log("option, input", option, input);
              return option?.children
                ?.toLowerCase?.()
                .includes?.(input.toLowerCase());
            }}
          />
        </Col>
        <Col xs={24} md={12}>
          <FormInput
            variant="underlined"
            name="zipCode"
            label="ZIP Code"
            placeholder="ZIP Code"
            required={true}
            normalize={(value) => {
              return (value || "").toUpperCase();
            }}
            formItemStyles={{ marginBottom: 0 }}
          />
        </Col> */}
        {/* <Col xs={0} md={12}></Col> */}

        {/* {!!currentUserHasAccount && <Col md={12} xs={0} order={9}></Col>} */}
        <Col span={12} order={9}>
          <FormInputArea
            initialValue={
              order?.serviceAddress ? currentUser?.instructions : ""
            }
            variant="underlined"
            rows={5}
            name="instructions"
            label="Address Instructions"
            placeholder="Add Instructions"
            formItemStyles={{ marginBottom: 0 }}
          />
        </Col>
        <Col xs={24} md={12} order={!!currentUserHasAccount ? 8 : 10}>
          <FormSelect
            options={trafficSource ?? []}
            label="How did you hear about us?"
            variant="underlined"
            name="marketingSource"
            placeholder="Select an option"
            renderLabel={"sourceName"}
            renderValue={"sourceName"}
            formItemStyles={{ marginBottom: 0 }}
            required={trafficSource?.length > 0}
          />
        </Col>
        <Col span={24} order={11}>
          <FormButton
            text={currentUserHasAccount ? "Update" : "Save"}
            type="submit"
            loading={isRegisteringUser || isUpdatingUser}
            style={{ padding: " 16px 50px", borderRadius: 50 }}
          // disabled={currentUserHasAccount}
          />
        </Col>
      </Row>
      <FormInput name="inviterId" hidden={true} />
      <FormInput hidden={true} name="countryId" initialValue={COUNTRY_ID} />
      <PendingRegistrationModal
        visible={!!pendingRegistration}
        email={pendingRegistration?.email}
        sending={sendingWelcomeEmail}
        onSend={handleSendWelcomeEmail}
        onCancel={() => setPendingRegistration(null)}
      />
    </Form>
  );
}

export default RegisterForm;
